import { ProjectData } from "grapesjs";

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const MOCK_DELAY = 1000;

// export interface ProjectData {
// 	[key: string]: any;
// }

export type CurationData = {
  id: string;
  html: string;
  css: string;
  projectData: ProjectData | null;
  created: string;
  editorVersion: string;
};

export type Curation = {
  id: string;
  name: string;
  slug: string;
  owner: string;
  published?: CurationData | null;
  draft: CurationData;
  history: CurationData[];
};

const mockServer = (() => {
  const _data: Record<string, Curation> = {};

  return {
    key: "mockServer",
    loaded: false,
    version: 0,
    data: _data,
  };
})();

export const useMockServer = () => {
  if (mockServer.loaded === false) {
    const data = JSON.parse(localStorage.getItem(mockServer.key) ?? "{}");
    Object.assign(mockServer.data, data);
    mockServer.loaded = true;
    console.log("🌈 MOCK SERVER LOADED", mockServer.data);
  }

  const saveData = () => {
    localStorage.setItem(mockServer.key, JSON.stringify(mockServer.data));
  };

  const createNewCuration = async (name?: string) => {
    await sleep(MOCK_DELAY);
    const uuid = Date.now().toString();
    mockServer.data[uuid] = {
      id: uuid,
      name: name ?? `New Curation ${uuid}`,
      slug: `new-curation-${uuid}`,
      owner: "",
      published: null,
      draft: {
        id: uuid,
        html: "",
        css: "",
        projectData: null,
        created: new Date().toISOString(),
        editorVersion: "0",
      },
      history: [],
    };

    console.log("Create new curation", mockServer.data[uuid]);

    saveData();
    return mockServer.data[uuid];
  };

  const cloneCuration = async (id: string) => {
    await sleep(MOCK_DELAY);
    if (mockServer.data[id] == null) {
      throw new Error(`Curation with id ${id} not found`);
    }

    const curation = mockServer.data[id];
    const uuid = Date.now().toString();
    mockServer.data[uuid] = {
      ...curation,
      id: uuid,
      name: `Clone of ${curation.name}`,
      slug: `clone-of-${curation.slug}`,
    };

    saveData();
    return mockServer.data[uuid];
  };

  const listCurations = async () => {
    await sleep(MOCK_DELAY);
    return Object.values(mockServer.data);
  };

  const getCuration = async (id: string) => {
    await sleep(MOCK_DELAY);
    if (mockServer.data[id] == null) {
      throw new Error(`Curation with id ${id} not found`);
    }

    return mockServer.data[id];
  };

  const updateCuration = async (id: string, data: Partial<Curation>) => {
    await sleep(MOCK_DELAY);
    if (mockServer.data[id] == null) {
      throw new Error(`Curation with id ${id} not found`);
    }
    mockServer.data[id] = {
      ...mockServer.data[id],
      ...data,
    };
    saveData();
    return mockServer.data[id];
  };

  const publishCuration = async (id: string) => {
    await sleep(MOCK_DELAY);
    if (mockServer.data[id] == null) {
      throw new Error(`Curation with id ${id} not found`);
    }
    const published = mockServer.data[id].published;
    if (published != null) {
      mockServer.data[id].history.push(published);
    }

    mockServer.data[id].published = mockServer.data[id].draft;
    saveData();
    return mockServer.data[id].published;
  };

  const deleteCuration = async (id: string) => {
    await sleep(MOCK_DELAY);
    if (mockServer.data[id] == null) {
      throw new Error(`Curation with id ${id} not found`);
    }

    delete mockServer.data[id];
    saveData();
  };

  return {
    mockServer,
    listCurations,
    createNewCuration,
    getCuration,
    updateCuration,
    publishCuration,
    cloneCuration,
    deleteCuration,
  };
};
